import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BlurOn,
  VolumeOff,
  Movie,
  AutoAwesome,
  VisibilityOff,
  GraphicEq,
  Highlight,
  ContentCut,
} from "@mui/icons-material";

const promptScenes = [
  {
    prompt: "Highlight the funny moments in my stream.",
    results: [
      { label: "Audience reaction detection", icon: GraphicEq },
      { label: "Smart clip generation", icon: Movie },
      { label: "Auto-highlights", icon: Highlight },
    ],
  },
  {
    prompt: "Blur every face except mine in real time.",
    results: [
      { label: "Face detection", icon: VisibilityOff },
      { label: "Selective blur", icon: BlurOn },
      { label: "Privacy shield", icon: AutoAwesome },
    ],
  },
  {
    prompt: "Mute any profanity from my guests automatically.",
    results: [
      { label: "Profanity muting", icon: VolumeOff },
      { label: "Demonetization protection", icon: AutoAwesome },
      { label: "Brand-safe audio", icon: GraphicEq },
    ],
  },
  {
    prompt: "Turn this VOD into viral TikTok and Shorts clips.",
    results: [
      { label: "Long-to-short clipping", icon: ContentCut },
      { label: "Vertical reframe", icon: Movie },
      { label: "Caption burn-in", icon: AutoAwesome },
    ],
  },
];

// Typewriter hook — types each char of `text` over `speed` ms each, pauses, then triggers onDone
function useTypewriter(text, speed = 32, onDone, dependencies = []) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    setTyped("");
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        const t = setTimeout(() => onDone?.(), 2200);
        return () => clearTimeout(t);
      }
    }, speed);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, ...dependencies]);

  return typed;
}

function PromptResultStage() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing -> results -> resetting

  const scene = promptScenes[sceneIdx];

  const typed = useTypewriter(
    scene.prompt,
    28,
    () => setPhase("results"),
    [sceneIdx]
  );

  // After results display, advance to next scene
  useEffect(() => {
    if (phase !== "results") return;
    const t = setTimeout(() => {
      setPhase("typing");
      setSceneIdx((i) => (i + 1) % promptScenes.length);
    }, 3200);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Prompt input mock */}
      <div className="relative rounded-2xl bg-zinc-900 ring-1 ring-zinc-800 shadow-xl px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          <span className="ml-2 text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500">
            zenstream — natural language
          </span>
        </div>

        <div className="flex items-start gap-3">
          <AutoAwesome className="!text-[20px] text-white mt-1 shrink-0" />
          <p
            className="font-mono text-sm sm:text-base md:text-lg text-zinc-100 leading-relaxed min-h-[3rem] sm:min-h-[3.5rem]"
            aria-live="polite"
          >
            {typed}
            <span className="inline-block w-[2px] h-4 sm:h-5 bg-white ml-0.5 align-middle animate-pulse" />
          </p>
        </div>

        {/* Spew-out arrow */}
        <AnimatePresence>
          {phase === "results" && (
            <motion.div
              key={`arrow-${sceneIdx}`}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/70"
            >
              <span className="h-px w-8 bg-white/40" />
              applying
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Result chips spewing out */}
      <div className="mt-6 sm:mt-8 min-h-[140px] sm:min-h-[120px] flex flex-wrap gap-3 justify-center">
        <AnimatePresence mode="popLayout">
          {phase === "results" &&
            scene.results.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={`${sceneIdx}-${r.label}`}
                  initial={{ opacity: 0, y: -16, scale: 0.9 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      delay: i * 0.18,
                      type: "spring",
                      stiffness: 320,
                      damping: 22,
                    },
                  }}
                  exit={{
                    opacity: 0,
                    y: 8,
                    scale: 0.96,
                    transition: { duration: 0.25 },
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 shadow-sm pl-3 pr-4 py-2 text-sm font-medium text-zinc-800"
                >
                  <span className="inline-flex items-center justify-center w-8 h-6 rounded-full bg-black text-white">
                    <Icon className="!text-[14px]" />
                  </span>
                  {r.label}
                </motion.div>
              );
            })}
        </AnimatePresence>
      </div>

      {/* Scene dots */}
      <div className="mt-4 flex justify-center gap-2">
        {promptScenes.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setSceneIdx(i);
              setPhase("typing");
            }}
            aria-label={`Show scene ${i + 1}`}
            aria-current={sceneIdx === i}
            className={`h-1.5 rounded-full transition-all ${
              sceneIdx === i
                ? "w-8 bg-black"
                : "w-3 bg-neutral-300 hover:bg-neutral-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function NaturalLanguageEditingShowcase() {
  return (
    <section
      id="natural-language-editing"
      aria-label="Natural-language video editing — describe edits in plain English"
      className="relative py-16 sm:py-24 px-4 bg-white overflow-hidden font-display"
    >
      <div className="max-w-6xl mx-auto">
        <header className="mb-12 sm:mb-16 text-center">
          <p className="mb-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-black">
            Natural-language editing
          </p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-black font-display">
            Automate your streams to <em className="italic font-medium">videos</em>.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-neutral-600 leading-relaxed">
            Type the edit, ship the edit. Zenstream turns plain-English prompts
            like{" "}
            <span className="text-black font-medium">
              “Highlight the funny moments in the stream”
            </span>{" "}
            into image censoring, profanity muting, face blur, and viral
            clip generation — automatically.
          </p>
        </header>

        {/* Animated prompt → results stage */}
        <PromptResultStage />

     
      </div>
    </section>
  );
}
